import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import styles from "../styles/Legal.module.css";
import { buildCanonicalUrl } from "../utils/basePath";

const TermsOfService: React.FC = () => {
  return (
    <>
      <SEO
        title="Terms of Service - Rythm"
        description="End User Agreement and Terms of Use for Rythm - Understand our terms and conditions when using the Rythm music sharing app."
        canonicalUrl={buildCanonicalUrl("/terms-of-service")}
        keywords="rythm terms of service, user agreement, terms and conditions, eula, end user license"
        ogImage="https://rythm.fm/assets/images/terms-og.png"
        ogImageAlt="Rythm Terms of Service - User agreement and terms of use"
        schemaType="WebPage"
      />
      <Header />
      <div className={styles.legalContainer}>
        <h1>End User Agreement and Terms of Use</h1>
        <p className={styles.lastUpdated}>Last Updated: May 31, 2024</p>
        <div className={styles.legalContent}>
          <p>
            This End User Agreement and Terms of Use (this "Agreement") is in
            place to govern your access to and use of the Rythm-operated
            websites and mobile applications (apps), including the sites, pages,
            and apps that link to this Agreement, such as the https://rythm.fm/
            site and the Rythm app (all of these sites, pages, and apps are
            referred to collectively in this Notice as the "Services," which
            also includes all music, images, video, text, or other material and
            content available through the Services (collectively, "Content"),
            and all associated software, functionality, and services offered on
            or through the Services).
          </p>
          <p>
            References to "Rythm," "us," "we," or "our" in this Agreement refer
            to Rythm Inc., and references to "you" and "your" refer to you as a
            visitor to and user of our Services.
          </p>

          <section>
            <h2>1. ACCEPTANCE OF THIS AGREEMENT; BINDING CONTRACT</h2>
            <p>
              Your access to and use of the Services are subject to this
              Agreement. YOU SHOULD READ THIS AGREEMENT COMPLETELY AND CAREFULLY
              BEFORE USING THE SERVICES. BY USING OUR SERVICES IN ANY WAY. BY
              INSTALLING OR OTHERWISE USING THE SERVICES OR THE SOFTWARE, YOU
              ACKNOWLEDGE THAT YOU HAVE READ AND UNDERSTOOD THIS AGREEMENT, AND
              AGREE TO BE BOUND BY ITS TERMS AND CONDITIONS. If you do not agree
              to (or cannot comply with) the terms and conditions of this
              Agreement, do not install, copy, or use the Services, software or
              any Content.
            </p>
          </section>

          <section>
            <h2>2. ADDITIONAL TERMS</h2>
            <p>
              In addition to this Agreement, your use of the Services is also
              subject to the terms of any other policies and legal notices
              posted on our site or in the Rythm app and made accessible to you
              through your use of the site and/or app ("Additional Terms").
              These Additional Terms include or may include:
            </p>
            <ul>
              <li>
                Our <Link to="/privacy">Privacy Notice</Link>, which describes how
                we collect, use, and disclose personal data you provide to us,
                as well as your choices regarding your use of such personal
                data; and
              </li>
              <li>
                Any additional terms we may post from time to time relating to
                the Services or any products or services made available on or
                through the Services, including for example any terms applicable
                to trial subscriptions ("Trial Terms").
              </li>
            </ul>
            <p>
              By using the Services, you also consent to and agree to comply
              with all Additional Terms. If any of these Additional Terms
              conflict with anything in this Agreement, the Additional Terms
              will take precedence.
            </p>
          </section>

          <section>
            <h2>3. ABOUT RYTHM</h2>
            <p>
              Rythm provides an online group listening and communication service
              (the "Subscription Service"), in which (subject to the applicable
              subscription tier) users may listen to and/or choose music tracks
              and other Content using their computer, tablet, and/or smartphone
              (a "device"), all within a virtual "listening session"
              environment, for non-commercial entertainment purposes. You may
              purchase a subscription directly from Rythm (or through a third
              party) on a monthly basis in advance. All subscriptions are
              subject to this Agreement.
            </p>
            <p>
              You understand that your ability to use the Services is subject to
              your device meeting the requirements for download (including
              technical capabilities and available storage space). In addition,
              you understand that functioning of certain features of the
              Services will depend on external factors, such as reception
              service areas, and failures or outages in telephone or internet
              service, over which we have no control.
            </p>
            <p>
              There may be limitations on your ability to access certain
              Content. Other details of the Subscription Service's features and
              functionality will be included on our site and in the Rythm app,
              and we undertake to regularly update them to reflect changes and
              improvements to the Subscription Service (and we reserve the right
              to modify the features and content of the Services and/or the
              terms of paid subscriptions at any time for any reason).
            </p>
            <p>
              Please note that the Services (or certain parts of the Services)
              may not be available in certain countries or via certain
              platforms.
            </p>
          </section>

          <section>
            <h2>4. SUBSCRIPTION SERVICE AND PAYMENT</h2>
            <ol>
              <li>
                <strong>Creating a User Account.</strong> To subscribe for and
                use the Services, you may be required to create an account, with
                a unique User ID and password. You are solely responsible for
                maintaining the confidentiality of your account and password and
                for restricting access to the Services and to your device. You
                agree to keep your password secure and confidential and to not
                allow anyone else to use your email address, User ID, or
                password to access the Services or any Content; to not do
                anything which would assist anyone else to gain access to any
                secured area of the Services; and to not create additional
                registration accounts for the purpose of abusing the
                functionality of the Services or any Content, or other users, or
                to seek to pass yourself off as another user. If you think
                someone else has obtained your account details, please let us
                know immediately via{" "}
                <a href="mailto:support@rythm.fm">support@rythm.fm</a> and we
                will close your account as quickly as possible. Please note that
                you will be responsible to Rythm and to others for all activity
                that occurs under your Rythm account. You agree to provide only
                true, accurate and complete information to us via the Services,
                and you will timely provide to Rythm any updated or corrected
                information and ensure such information is correct. Any personal
                information provided by you will be treated in accordance with
                our <Link to="/privacy">Privacy Notice</Link>.
              </li>
              <li>
                <strong>Free Trials.</strong> Users may be eligible for a free
                short-term trial of the Subscription Service (a "Trial"),
                subject to the terms of this Agreement and any Trial Terms or
                other Additional Terms. A Trial provides you with access to the
                Subscription Service for the notified period, starting from the
                moment you accept the offer. When you begin a Trial, you may be
                required to submit payment information through the platform you
                use to access the Subscription Service (Discord, Apple Pay,
                Google Play, or other). By submitting payment information, you
                accept the Trial offer and you agree that at the expiration of
                the Trial period, your payment information may be used to enroll
                you into the paid Subscription Service on a monthly
                automatically-renewable basis, unless you validly terminate your
                Trial in accordance with the Trial Terms before the end of the
                Trial period. You may accept a Trial offer only once, and Rythm
                reserves the right to withdraw, modify, or cease the Trial at
                any time without notice or liability to you.
              </li>
              <li>
                <strong>Subscription Service.</strong> If you subscribe for the
                Subscription Service (including automatically upon expiration of
                a Trial), you undertake to pay all required fees as set out from
                time to time on the relevant pages of the Services ("Fees").
                Fees will be subject to applicable taxes unless expressly stated
                otherwise on the Services. Fees must be paid in full in advance
                of each calendar month and the continuation of your Subscription
                Services is subject to payment of Fees. Each subscription will
                automatically renew at the end of the applicable subscription
                period, unless cancelled before the end of the then-current
                subscription period. You may contact us via{" "}
                <a href="mailto:support@rythm.fm">support@rythm.fm</a> for
                assistance with or instructions on how to cancel. Any
                cancellation will take effect the day after the last day of the
                current subscription period, and you will be downgraded to the
                free version of the Services. We do not provide refunds or
                credits for any partial subscription periods, except as
                expressly stated in this Agreement or any Additional Terms. Fees
                are subject to change by posting new Fees on the Services at any
                time. Subject to applicable law, by continuing to use the
                Subscription Service after a price change has taken effect, you
                are deemed to accept the new price and to agree to pay the
                updated Fees. If you do not agree to a price change, you may
                cancel your subscription prior to the price change coming into
                effect. Tax rates are based on rates applicable at the time of
                your monthly charge but such amounts may change at any time
                pursuant to local tax requirements and any such change will be
                automatically applied based on the account information you
                provide.
              </li>
              <li>
                <strong>Payments.</strong> Payments will be processed by the
                platform you use to obtain the Subscription Service (Discord,
                Apple Pay, Google Play, or other). Rythm does not process your
                payment directly. All transactions shall be charged immediately,
                and once your payment is made, it is non-refundable. Your
                payment may be subject to validation checks and authorization by
                your bank or card issuer. Fees are listed in local currency in
                some instances but all payments will be processed in US dollars
                (subject to currency exchange rates and costs where applicable).
                You agree to be responsible for any costs or extra fees incurred
                for funds transfers or payment services are payable by you. We
                reserve the right to suspend or otherwise limit your access to
                the Subscription Service if payment is not received, including
                if collection of the Fees is not commercially or practically
                viable for any reason.
              </li>
            </ol>
          </section>

          <section>
            <h2>5. LICENSE GRANT; INTELLECTUAL PROPERTY</h2>
            <p>
              The Services may enable you to obtain, listen to, view, and/or
              read (as the case may be) Content that may be obtained by you in
              digital form, and you shall do so solely for your personal,
              non-commercial entertainment use. You acknowledge and agree that
              Content may be owned by Rythm or by third parties, including
              Rythm's third-party Content providers ("Content Licensors"). In
              all circumstances, you understand and acknowledge that your rights
              with respect to Content is limited by applicable copyright and
              other intellectual property law.
            </p>
            <p>
              Subject to the terms of this Agreement (and, where applicable,
              payment of any and all required Fees), Rythm grants to you a
              personal, non-exclusive, non-transferable, limited and revocable
              licence to use the Services (and the Content solely via the
              Services) for your own personal, non-commercial use. You may not
              (i) sell, sublicense, publish, transfer, assign, or otherwise
              (re)distribute all or any portion of the Services or any Content
              in any form, including via distribution or posting via the
              internet or any mobile network or device or any other method
              (further including by way of downloading, copying, broadcasting,
              playing, presenting, sharing, making available or other technical
              means); (ii) copy, decompile, reverse engineer, disassemble,
              attempt to derive the source code of, modify, create derivative
              works, or otherwise circumvent the technology of the Services, any
              Content, or any part thereof; or (iii) engage in
              advertising-related use of the Services or any Content or any
              other use which may constitute infringement of any intellectual
              property or other proprietary rights (including any use which
              could be considered a promotion or encouragement of any brand,
              product or service). Without limiting the foregoing or any other
              provision of this Agreement or any Additional Terms, you will not
              use or attempt to use or access Content or any other part of the
              Services other than in accordance with the terms of the license
              granted hereunder.
            </p>
            <p>
              The Services are owned and operated by Rythm, and we and our
              Content Licensors own or control all relevant intellectual
              property rights in the Services and the Content. You acknowledge
              and agree that you will not acquire or be deemed to acquire under
              this Agreement or otherwise any ownership of any intellectual
              property or other proprietary rights in the Services and/or any
              Content, and that any and all intellectual property and other
              proprietary rights and to the Services, the Content, and any
              branding therein (including trademarks, brands and logos) belong
              to Rythm and/or our Content Licensors. You agree and accept that
              any use of the Services and/or any Content in any way that may be
              inconsistent with the terms of this Agreement will be treated as
              an unauthorized use of the Services and/or such Content (and each
              copyright work therein) and may result in a claim by Rythm (or a
              third party rightsholder such as our Content Licensors) against
              you (or your related parties) for infringement of the copyright
              and/or other rights in such works.
            </p>
            <p>
              The trademarks, service marks, brands, and logos used in the
              Services and/or the Content (including, without limitation,
              "Rythm") are trademarks or trade names of Rythm, our Content
              Licensors, or our other licensors, unless otherwise stated. You
              may not use any of our or their trademarks, service marks, brands,
              or logos without our express prior written permission. Further,
              you may not use any metatags, meta elements, "hidden text" or
              other equivalents using our or their trademarks or any other
              colorable equivalent without our prior written authorization. You
              also may not remove, delete, alter or obscure any trademarks or
              any copyright, trademark, patent or other intellectual property or
              proprietary rights notices from the Services or any Content. Any
              attempt by you to take any such action is a violation of our
              rights and of this Agreement.
            </p>
          </section>

          <section>
            <h2>6. USER CONDUCT</h2>
            <p>
              You agree to obey all applicable laws when using the Services. In
              addition, you agree that you are responsible for the content,
              materials, information, and/or communications you send to or
              initiate via the Services ("User Materials"). Without limiting the
              foregoing or any other provision of this Agreement or any
              Additional Terms, you agree not to (and not to attempt to),
              directly or indirectly:
            </p>
            <ol>
              <li>
                participate in any form of activity or transmit any User
                Materials which are (or might be likely to be) unlawful,
                harassing, libellous, defamatory, abusive, threatening, harmful,
                vulgar, obscene, profane, or sexually or racially-offensive, or
                that otherwise includes objectionable material, or that would
                infringe the copyright or other intellectual property or
                proprietary rights of any person;
              </li>
              <li>use the Services to engage in any commercial activities;</li>
              <li>
                provide your login details or other information to any third
                party or otherwise assist or permit any unauthorized access to
                the Service;
              </li>
              <li>
                register more than one account for yourself or anyone else;
              </li>
              <li>
                input or transmit User Materials or other content that contains
                software viruses or any other computer code, files, or programs
                that are designed or intended to disrupt, damage, or limit the
                functioning of any software, hardware, or telecommunications
                equipment or to damage or obtain unauthorized access to any data
                or other information of any third party;
              </li>
              <li>
                for any reason whatsoever, reverse engineer, decompile,
                disassemble, or otherwise tamper with any security components,
                usage rules or other protection measures applicable to the
                Services or Content;
              </li>
              <li>
                make any use of any Content that would infringe the copyright or
                other intellectual property or proprietary rights therein;
              </li>
              <li>
                make any recording or reproduction of, and will not stream or
                transmit or make available, any Content except as permitted
                hereunder;
              </li>
              <li>
                amplify, transmit or retransmit the Services or any Content so
                as to be audible or viewed outside of your own personal space;
              </li>
              <li>
                redistribute, broadcast, publicly perform or publicly display
                any Content, or otherwise transfer any Content obtained through
                the Services;
              </li>
              <li>
                modify any software or Content obtained through the Services for
                any reason whatsoever, including for the purpose of disguising
                or changing any indications of the ownership or source of the
                Content or removing (or trying to remove) and "DRM" (digital
                rights management) information or metadata, save as permitted by
                applicable law;
              </li>
              <li>
                input or transmit any User Materials or other content that
                contains anything which could be used to determine or alter the
                architecture of the Services, or could be used to decompile,
                disassemble, or reverse engineer the Services;
              </li>
              <li>
                collect personal data about other users or any other person (for
                any purpose);
              </li>
              <li>
                use automated means, including spiders, robots, crawlers, data
                mining tools, or the like to download data from the Services;
              </li>
              <li>
                impose an unreasonable or disproportionately large load on our
                infrastructure; or
              </li>
              <li>
                gain unauthorized access to Rythm's computer systems or engage
                in any activity that disrupts, diminishes the quality of,
                interferes with the performance of, or impairs the functionality
                of, the Services or any Content.
              </li>
            </ol>
            <p>
              For the avoidance of doubt, you agree to abide by the above, and
              by all other rules and policies established from time to time by
              Rythm, which are applied by Rythm in a non-discriminatory manner
              to all users of the Services, and may include, for example,
              required or automated updates, modifications, and/or
              reinstallations of the software and obtaining available patches to
              address security, interoperability, and/or performance issues. You
              are responsible for all User Materials and other content that you
              transmit via the Services.
            </p>
            <p>
              By transmitting User Materials or other content via or to the
              Services, you grant to Rythm a non-exclusive, transferable,
              sub-licensable, royalty-free, irrevocable, fully-paid, worldwide
              license to copy, communicate to the public, make available, create
              derivative works from, distribute and/or otherwise use that
              content via any media, alone or together with other materials, in
              connection with the Services. Where applicable and to the fullest
              extent of applicable law, you hereby waive (and agree not to
              enforce) any so-called "moral rights" (or equivalent) for such use
              by Rythm.
            </p>
          </section>

          <section>
            <h2>7. MONITORING AND ENFORCEMENT</h2>
            <p>We have the right (but not the obligation) to:</p>
            <ul>
              <li>
                Monitor compliance with this Agreement and any Additional Terms;
              </li>
              <li>
                Take any action with respect to any or event that we deem
                necessary or appropriate in our sole discretion, including if we
                believe that event violates this Agreement or any Additional
                Terms, infringes any intellectual property right or other right
                of any person or entity, threatens the personal safety of users
                of the Services or the public, or could create liability for us
                or any third party;
              </li>
              <li>
                Take appropriate legal action, including referral to law
                enforcement, for any illegal or unauthorized use of the Services
                or any illegal or unauthorized event; and
              </li>
              <li>
                Terminate or suspend your access to all or part of the Services
                if you violate this Agreement or any Additional Terms.
              </li>
            </ul>
            <p>
              We reserve the right to take any action we may deem appropriate in
              our sole discretion with respect to violations or enforcement of
              this Agreement and/or any Additional Terms, and we expressly
              reserve all rights and remedies available to us at law or in
              equity. Users are warned that breach of this Agreement and/or any
              Additional Terms may lead to civil or criminal liability. You
              acknowledge that we will cooperate fully with investigations of
              violations of systems or network security at other sites,
              including cooperating with law enforcement authorities in
              investigating suspected criminal violations. In addition, we will
              cooperate with the authorities in prosecuting any user who
              Interferes with the Services or any Content or otherwise attempts
              to defraud Rythm or any other parties through your use of the
              Services or any Content or any other services provided hereunder.
              We have no liability or responsibility to anyone for performance
              or nonperformance of the activities described in this section.
            </p>
          </section>

          <section>
            <h2>8. TERMINATION</h2>
            <p>
              This Agreement, and all licenses granted by this Agreement may be
              terminated:
            </p>
            <ul>
              <li>
                By you, by deleting the Rythm app from your device and ceasing
                to access or use the Services; or
              </li>
              <li>
                By us, at any time or without notice for any reason or not
                reason, including if we cease supporting the Services or if you
                violate (or act in a manner indicating that you do not intend to
                or are unable to comply with) this Agreement or any Additional
                Terms, if we are legally required to do so by law, or if
                continuation is likely to be no longer commercially viable, or
                if you use any Content in a manner inconsistent with the terms
                hereunder or which otherwise may constitute infringement of any
                rights of Rythm or our Content Licensors.
              </li>
            </ul>
            <p>
              In addition, Rythm reserves the right to suspend provision of
              services in the event of an actual or suspected breach of this
              Agreement or other unauthorized use of the Services.
            </p>
            <p>
              Termination or suspension by Rythm is without prejudice to any
              other rights or remedies we may have.
            </p>
            <p>
              Upon termination, all rights granted to you under this Agreement
              will terminate, and you may no longer have access to any of the
              information associated with your account. Immediately upon any
              termination: (a) any unpaid Fee(s) shall remain payable in full
              (for the full period of each license granted), and you shall not
              be entitled to any refund; and (b) you shall cease use of the
              Services and Content, shall delete the Rythm app from your device,
              and shall delete all copies of any Content in your possession or
              control.
            </p>
          </section>

          <section>
            <h2>9. TECHNICAL REQUIREMENTS</h2>
            <p>
              You agree to use the Services in accordance with any technical
              guidelines that may be provided to you (and/or modified by us)
              from time to time. We will not be held responsible for any
              provision of service or electronic delivery that is incompatible
              with your operating system or other problems specific to your
              device. Rythm reserves the right to impose limited periods of
              downtime for maintenance or other interruptions for commercial
              reasons without prior notice. Specifically, you accept and agree
              that the Services may be interrupted from time to time due to
              technical or other issues caused by a telecoms or internet service
              provider or by network congestion, and that Rythm may temporarily
              suspend provision or operation of the Services for reasonable
              carrying out of periodic maintenance where necessary or where
              preferable to improve the Services. You expressly agree that Rythm
              shall not be liable for any loss resulting from any such downtimes
              or interruptions. If you are in doubt as to whether or not the
              Services are suitable for your requirements, please carefully
              evaluate the Services before registering as a User. If you are
              still unsure, you may contact us via{" "}
              <a href="mailto:support@rythm.fm">support@rythm.fm</a> for further
              information.
            </p>
          </section>

          <section>
            <h2>10. FILTERING TOOLS</h2>
            <p>
              When using the Services, you may be able to use filtering tools to
              limit explicit Content. However, despite our use of filtering
              tools, some explicit content may be served to you and accordingly
              (to the extent permitted by applicable law) you must not rely on
              the Services to filter all explicit content. If you encounter or
              become aware of any objectionable or infringing or unlawful
              content available anywhere on or via the Services, please
              immediately report such material to Rythm (and the specific page
              on which it is found), or if you believe that you are the owner of
              the copyright or other rights in any material appearing on the
              Services, or if you have any other complaint about the Services or
              any Content or other posted materials, please contact us via{" "}
              <a href="mailto:support@rythm.fm">support@rythm.fm</a>
              (making sure to include both the Uniform Resource Locator (URL)
              for the non-complying content and the reasons you believe it does
              not comply). Please note that there may be adverse legal
              consequences if you make a false or bad faith allegation through
              this process.
            </p>
          </section>

          <section>
            <h2>11. YOUR WARRANTIES</h2>
            <p>
              By using the Services and/or creating an account, you affirm and
              warrant that you are 18 years of age or older, or, if you are not,
              that you are 13 years or older and have obtained parental or
              guardian consent to enter into these terms. Additionally, you
              represent and warrant that, at all times while using the Services,
              you will use the Services solely for your own personal,
              non-commercial entertainment purposes or your own device(s), and
              not for redistribution or transfer of any kind.
            </p>
          </section>

          <section>
            <h2>12. INDEMNIFICATION</h2>
            <p>
              You agree to indemnify, save, and hold us, our affiliates, Content
              Licensors, contractors, employees, agents, and other licensors and
              partners (collectively, the "Rythm Parties") harmless from any and
              all claims, losses, damages, liabilities, costs, and expenses
              (including legal fees and expenses) that may be incurred by any
              Rythm Party, arising out of or relating to your use or misuse of
              the Services and/or any Content, and any breach by you of this
              Agreement or any Additional Terms (including any representations
              and warranties made by you). We reserve the right, at your
              expense, to assume the exclusive defense and control of any matter
              for which you are required to indemnify us, and you agree to
              cooperate with our defense of these claims. We will use reasonable
              efforts to notify you of any such claim, action, or proceeding
              upon becoming aware of it. You undertake to inform Rythm
              immediately if you become aware of any possible breach of this
              Agreement or any Additional Terms by you or any other party.
            </p>
          </section>

          <section>
            <h2>13. NO WARRANTIES; DISCLAIMERS</h2>
            <ol>
              <li>
                <strong>No Warranties.</strong> THE SERVICES (INCLUDING ALL
                SOFTWARE, CONTENT AND OTHER INFORMATION, MATERIALS AND PRODUCTS
                INCLUDED ON OR OTHERWISE MADE AVAILABLE TO YOU THROUGH THE
                SERVICE) ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT
                WARRANTIES OF ANY KIND FROM RYTHM OR ANY CONTENT OWNERS. TO THE
                FULL EXTENT PERMISSIBLE BY APPLICABLE LAW, RYTHM AND ALL CONTENT
                OWNERS DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING,
                BUT NOT LIMITED TO, IMPLIED WARRANTIES OF MERCHANTABILITY AND
                FITNESS FOR A PARTICULAR PURPOSE. NEITHER RYTHM NOR ANY CONTENT
                OWNER WARRANTS THAT THE SERVICES OR ANY SOFTWARE, CONTENT,
                INFORMATION, MATERIALS OR PRODUCTS INCLUDED ON OR OTHERWISE MADE
                AVAILABLE TO YOU THROUGH THE SERVICES ARE FREE OF VIRUSES OR
                OTHER HARMFUL COMPONENTS.
              </li>
              <li>
                <strong>Content Disclaimer.</strong> Although our goal to
                provide accurate and up-to-date information, the Content may not
                be wholly accurate or up-to-date, complete or free of defects,
                and is subject to change at short notice. All Content is
                provided without any representations or warranties of any kind
                (implied or express) to the fullest extent permitted by
                applicable law. The Services are intended only for the purposes
                specified or implied therein, and your use of the Services
                and/or any Content is entirely at your own risk. Rythm makes no
                warranty or representation about the provision or availability
                at any particular item of Content. You acknowledge that Rythm
                and the Content Owners reserve the right to remove or cease
                provision or availability (with or without notice) of any
                Content at any time without notice and without liability to you.
              </li>
              <li>
                <strong>Functionality Disclaimer.</strong> Rythm makes no
                warranty that: (a) the Services will meet your requirements; (b)
                the Services will be uninterrupted, timely, secure, or
                error-free; (c) the results obtained from the use of the
                Services will be accurate, complete, reliable or effective; or
                (d) the quality of any content or services obtained by you from
                the Services, from us, or from any third parties' websites or
                applications to which the Services may link, will meet your
                expectations or be free from mistakes, errors or defects. The
                use of the Services is at your own risk and with your agreement
                that you will be solely responsible for any loss or damage that
                results from such activities.
              </li>
              <li>
                <strong>Limitations.</strong> SOME JURISDICTIONS DO NOT ALLOW
                THE EXCLUSIONS OR LIMITATIONS ON IMPLIED WARRANTIES OR
                LIMITATIONS ON THE APPLICABLE STATUTORY RIGHTS OF A CONSUMER, SO
                SOME OR ALL OF THE ABOVE EXCLUSIONS AND LIMITATIONS MAY NOT
                APPLY TO YOU. IN SUCH CASES, ALL AVAILABLE EXCLUSIONS AND
                LIMITATIONS SHALL APPLY TO THE FULLEST EXTENT PERMITTED BY
                APPLICABLE LAW.
              </li>
            </ol>
          </section>

          <section>
            <h2>14. LIMITATION OF LIABILITY</h2>
            <ol>
              <li>
                <strong>General.</strong> You agree that (subject to applicable
                law), your sole and exclusive remedy for any dissatisfaction or
                problem arising from or in connection with the Services is to
                uninstall and stop using the Services.
                <p>
                  TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, NEITHER
                  RYTHM NOR ANY CONTENT OWNER WILL BE LIABLE FOR ANY INCIDENTAL,
                  PUNITIVE, SPECIAL OR CONSEQUENTIAL DAMAGES OF ANY KIND ARISING
                  FROM THE USE OF THE SERVICES OR FROM SOFTWARE, CONTENT,
                  INFORMATION, MATERIALS OR PRODUCTS INCLUDED ON OR OTHERWISE
                  MADE AVAILABLE TO YOU THROUGH THE SERVICES, OR FOR ANY DAMAGES
                  IN EXCESS OF THE AMOUNT PAID FOR THE SPECIFIC ITEM OF CONTENT
                  GIVING RISE TO THE APPLICABLE CLAIM FOR DAMAGES.
                </p>
                <p>
                  THE ABOVE LIMITATIONS WILL APPLY WHETHER SUCH DAMAGES ARISE
                  OUT OF BREACH OF CONTRACT, TORT OR OTHERWISE, AND REGARDLESS
                  OF WHETHER SUCH DAMAGES WERE FORSEEABLE OR IF WE WERE ADVISED
                  OF THE POSSIBILITY OF SUCH DAMAGES. APPLICABLE LAW MAY NOT
                  ALLOW THE LIMITATION OR EXCLUSION OF LIABILITY OR INCIDENTAL
                  OR CONSEQUENTIAL DAMAGES, SO THE ABOVE LIMITATION OR EXCLUSION
                  MAY NOT APPLY TO YOU. IN SUCH CASES, OUR LIABILITY WILL BE
                  LIMITED TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW.
                </p>
              </li>
              <li>
                <strong>Device Charges.</strong> Without limiting any other
                provision of this Agreement, you acknowledge and agree that you
                are solely responsible for all charges imposed by your device
                carrier with respect to your use of the Services, including
                without limitation all text messaging charges, roaming and
                international charges, and usage and overage charges charged by
                your carrier. in no event will we have any liability to you or
                any other party relating to any charges imposed by your device
                carrier relating to your use of the Services.
              </li>
              <li>
                <strong>Rythm Parties.</strong> You expressly acknowledge that
                "we" and "Rythm" when used in Section 13 and Section 14 includes
                all Rythm Parties.
              </li>
            </ol>
          </section>

          <section>
            <h2>15. DISPUTE RESOLUTION</h2>
            <p>
              <strong>Jurisdiction.</strong> This Agreement shall be governed by
              and construed in accordance with the laws of Delaware, United
              States of America, without regard to conflict of laws provisions.
              You and Rythm hereby irrevocably submit to the exclusive
              jurisdiction of the courts of Delaware for the purpose of hearing
              and determining any dispute arising out of or in connection with
              the Agreement or its formation or validity.
            </p>
          </section>

          <section>
            <h2>16. UPDATES TO TERMS OF SERVICE</h2>
            <p>
              <strong>Updates to this Agreement.</strong> We may change this
              Agreement from time to time and without prior notice to you, for
              any reason including to reflect changes in applicable law, updates
              to the Services, or for other reasons, subject to applicable law.
              Such revised Agreement will be provided to you (as applicable, via
              the applicable platform, by updating the date at the top of the
              applicable terms and conditions, or via a notification via the
              Services). Your continued use of any part of the Services after
              the effective date of the revised Agreement represents your
              consent to the changes. You waive any right you may have to
              receive additional notice of such changes or modifications. If you
              do not agree to any change to these terms, please discontinue
              using the Services immediately.
            </p>
          </section>

          <section>
            <h2>17. GENERAL PROVISIONS</h2>
            <ol>
              <li>
                <strong>Entire Agreement.</strong> This Agreement, together with
                any Additional Terms, rules, guidelines and other Rythm terms
                incorporated or referred to herein, constitutes the entire
                agreement between you and Rythm pertaining to the subject matter
                hereof. In the event of a conflict between this Agreement and
                any Additional Terms that may apply to a specific software,
                product, or Service, the Additional Terms shall control with
                respect to your use of that particular software, product, or
                Service.
              </li>
              <li>
                <strong>No Implied Waiver.</strong> The failure of Rythm to
                exercise or enforce any right or provision of this Agreement
                shall not constitute a waiver of such right or provision, or of
                any other right or provision contained herein. If any provision
                of this Agreement is found by a court of competent jurisdiction
                to be invalid, the parties nevertheless agree that the court
                should endeavor to give effect to the parties' intentions as
                reflected in the provision, and the other provisions of this
                Agreement shall remain in full force and effect.
              </li>
              <li>
                <strong>Assignment.</strong> You may not assign your rights
                under this Agreement without Rythm's prior written consent, and
                any attempted assignment shall be null and void. Rythm may
                assign this Agreement without restriction and without notice to
                you.
              </li>
              <li>
                <strong>Notice.</strong> Rythm may provide you with notices,
                including those regarding changes to this Agreement, via email,
                regular mail or notifications within the Services. You may
                notify Rythm via postal mail to Rythm, Inc., 305 Broadway Floor
                7, New York, NY 10007, United States.
              </li>
              <li>
                <strong>Severability.</strong> If any provision of this
                Agreement is determined to be invalid or unenforceable, the
                provision shall be deemed to be severable from the remainder of
                this Agreement and will not cause the invalidity or
                unenforceability of the remainder of this Agreement.
              </li>
              <li>
                <strong>Language.</strong> This Agreement was originally
                prepared in the English language. Although we may provide one or
                more translations for your convenience, the English version
                shall control to the extent permitted by law in the event of a
                conflict or discrepancy.
              </li>
            </ol>
          </section>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default TermsOfService;
